
	import { Component, Vue, Watch } from 'vue-property-decorator';
	import MultidayCalendarDay from "@/components/reservation/MultidayCalendarDay.vue";
	import IResource from "@/IResource";

	interface CalendarDay {
		date: Date;
		dateKey: string;
		isToday: boolean;
		isCurrentMonth: boolean;
		isPast: boolean;
		isSelected: boolean;
	}

	@Component({
		components: {MultidayCalendarDay},
	})
	export default class MultidayCalendar extends Vue {

		public numberOfMonthsShown: number = 6;

		public startingMonth: number = 1;
		public startingYear: number = 2020;

		public selectionStart: Date | null = null;
		public selectionEnd: Date | null = null;

		public calendarView: CalendarDay[][][] = [];

		public monthNames: string[] = [
			'',
			'Januar',
			'Februar',
			'März',
			'April',
			'Mai',
			'Juni',
			'Juli',
			'August',
			'September',
			'Oktober',
			'November',
			'Dezember'
		];

		get availability() {
			return this.$store.state.availability;
		}

		get resource(): IResource {
			return this.$store.state.resource;
		}

		public yearOf(n: number) {
			return this.startingYear + Math.floor((this.startingMonth + n - 1) / 12)
		}

		public monthOf(n: number) {
			return ((this.startingMonth + n - 1) % 12 + 1)
		}

		public dateKey(date: Date): string {
			if (date) {
				return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
			}
			return '';
		}

		public formatTime(minutes: number): string {
			let mins: number|string = minutes % 60;
			if (mins < 10) {
				mins = "0" + mins;
			}
			let hours: number|string = Math.floor(minutes / 60);
			if (hours < 10) {
				hours = "0" + hours;
			}
			return hours + ':' + mins;
		}

		get canGoToNextStep(): boolean {
			if (this.selectionStart === null || (this.resource.checkin_time_enabled && this.selectionEnd === null)) {
				return false;
			}
			return true;
		}

		get canGoBack(): boolean {
			let today = new Date();
			return this.startingYear == today.getFullYear() && this.startingMonth > today.getMonth() + 1 || this.startingYear > today.getFullYear();
		}

		public goBack(steps: number): void {
			if (this.canGoBack) {
				let newMonth = this.startingMonth - steps;
				if (newMonth < 0) {
					this.startingMonth = (newMonth + 12) % 12;
					this.startingYear -= 1;
				} else if (newMonth == 0) {
					this.startingMonth = 12
					this.startingYear -= 1;
				} else {
					this.startingMonth = newMonth;
				}
				this.buildCalendar();
			}
		}

		public goForward(steps: number): void {
			let newMonth = this.startingMonth + steps;
			if (newMonth > 12) {
				this.startingMonth = newMonth % 12;
				this.startingYear += 1;
			} else {
				this.startingMonth = newMonth;
			}
			this.buildCalendar();
		}

		public onSelectDay(day: CalendarDay) {
			if (!day.isPast && day.isCurrentMonth) {
				if (this.selectionStart === null) {
					if (!this.canSelectDay(day.date)) return;
					this.selectionStart = day.date;
				} else {
					if (this.selectionEnd === null) {
						if (this.selectionStart > day.date) {
							// selecting backwards
							if (this.canSelectDayDetails(this.selectionStart).morning) {
								let checkDate = new Date(this.selectionStart);
								let enddate = day.date;
								do {
									checkDate.setDate(checkDate.getDate() - 1);
									let canSelect = this.canSelectDayDetails(checkDate);
									if (!canSelect.evening) {
										enddate = new Date(checkDate);
										enddate.setDate(enddate.getDate() + 1);
										break;
									}
									if (!canSelect.morning) {
										enddate = new Date(checkDate);
										break;
									}
								} while (checkDate > day.date);

								// swap dates for easier processing
								this.selectionEnd = this.selectionStart
								this.selectionStart = enddate;
							}
						} else {
							// selecting forwards
							if (this.canSelectDayDetails(this.selectionStart).evening) {
								let checkDate = new Date(this.selectionStart);
								let enddate = day.date;
								do {
									checkDate.setDate(checkDate.getDate() + 1);
									let canSelect = this.canSelectDayDetails(checkDate);
									if (!canSelect.morning) {
										enddate = new Date(checkDate);
										enddate.setDate(enddate.getDate() - 1);
										break;
									}
									if (!canSelect.evening) {
										enddate = new Date(checkDate);
										break;
									}
								} while (checkDate < day.date);
								this.selectionEnd = enddate;
							}
						}
					} else {
						if (!this.canSelectDay(day.date)) return;
						this.selectionStart = day.date;
						this.selectionEnd = null;
					}
				}
				this.buildCalendar();
			}
		}

		public canSelectDay(date: Date): boolean {
			let canSelect = this.canSelectDayDetails(date);
			return canSelect.morning || canSelect.evening;
		}

		public canSelectDayDetails(date: Date): { morning: boolean, evening: boolean } {
			let dateKey = this.dateKey(date);
			let morning: boolean = true;
			let evening: boolean = true;
			if (this.availability && dateKey in this.availability) {
				this.availability[dateKey].forEach((slot: [number, number]) => {
					if (this.resource.checkin_time_enabled) {
						if (slot[0] < this.resource.checkout_time || slot[1] <= this.resource.checkout_time) {
							morning = false;
						}
						if (slot[0] >= this.resource.checkin_time || slot[1] > this.resource.checkin_time) {
							evening = false;
						}
					} else {
						if (slot[0] < 720 || slot[1] <= 720) {
							morning = false;
						}
						if (slot[0] >= 720 || slot[1] > 720) {
							evening = false;
						}
					}
				});
			}
			return { morning: morning, evening: evening };
		}

		public goToNextPage(): void {
			if (this.selectionStart !== null) {
				this.$store.commit('SET_STARTDATE', this.selectionStart);
				if (this.selectionEnd === null) {
					this.$store.commit('SET_ENDDATE', this.selectionStart);
				} else {
					this.$store.commit('SET_ENDDATE', this.selectionEnd);
				}
				if (this.resource.checkin_time_enabled) {
					this.$store.commit('SET_FROMTIME', this.resource.checkin_time);
					this.$store.commit('SET_TOTIME', this.resource.checkout_time);
					this.$store.commit('SET_STEP', 'step3')
				} else {
					this.$store.commit('SET_STEP', 'step2')
				}
			}
		}

		public mounted() {
			let today = new Date();
			if (this.$store.state.startdate) {
				this.selectionStart = this.$store.state.startdate;
			}
			if (this.$store.state.enddate) {
				this.selectionEnd = this.$store.state.enddate;
			}
			this.startingYear = today.getFullYear();
			this.startingMonth = today.getMonth() + 1;
			this.buildCalendar();

			this.$gtag.pageview( {page_path: '/multiday-calendar' });
		}

		public buildCalendar() {
			let view: CalendarDay[][][] = [];
			for (let monthIndex = 0; monthIndex < this.numberOfMonthsShown; monthIndex++) {
				let firstDayOfMonth: Date = new Date(this.startingYear, this.startingMonth + monthIndex - 1, 1);
				let weekday = firstDayOfMonth.getDay();
				let offset = weekday - 1;
				if (weekday == 0) {
					offset = 6;
				}
				view[monthIndex] = [];
				for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
					view[monthIndex][weekIndex] = [];
					for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
						let calendarDayDate = new Date(this.startingYear, this.startingMonth + monthIndex - 1, 1 - offset + (dayIndex + 7 * weekIndex));
						let today = new Date();
						let isToday = false;
						if (today.getFullYear() == calendarDayDate.getFullYear() && today.getMonth() == calendarDayDate.getMonth() && today.getDate() == calendarDayDate.getDate()) {
							isToday = true;
						}
						let firstBookableDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), today.getHours() + this.resource.min_hours_to_reservation, today.getMinutes());
						let lastBookableDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), today.getHours() + (this.resource.max_hours_to_reservation || 87600), today.getMinutes());
						let isPast = calendarDayDate.toDateString() !== firstBookableDate.toDateString() && calendarDayDate < firstBookableDate || calendarDayDate > lastBookableDate;
						if (calendarDayDate.toDateString() === firstBookableDate.toDateString()) {
							let lastPossibleBookTime = new Date(calendarDayDate);
							lastPossibleBookTime.setHours(this.resource.endtime);
							if (lastPossibleBookTime < firstBookableDate) {
								isPast = true;
							}
						}
						let isCurrentMonth = false;
						if (firstDayOfMonth.getFullYear() == calendarDayDate.getFullYear() && firstDayOfMonth.getMonth() == calendarDayDate.getMonth()) {
							isCurrentMonth = true;
						}
						let isSelected = false;
						if (this.selectionStart !== null) {
							if (this.selectionEnd !== null) {
								isSelected = calendarDayDate >= this.selectionStart && calendarDayDate <= this.selectionEnd;
							} else {
								isSelected = calendarDayDate.toDateString() == this.selectionStart.toDateString();
							}
						}
						view[monthIndex][weekIndex][dayIndex] = {
							date: calendarDayDate,
							dateKey: this.dateKey(calendarDayDate),
							isToday: isToday,
							isCurrentMonth: isCurrentMonth,
							isSelected: isSelected,
							isPast: isPast
						}
					}
				}
			}
			this.calendarView = view;
		}
	}
